import { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const DISMISS_KEY = "lwp_install_nudge_dismissed";
const VISITS_KEY = "lwp_visits";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

// Subtle one-time "Add us to your home screen" banner for RETURNING visitors.
// Shows once (until dismissed/installed), only when the app is installable.
export default function InstallNudge() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosOpen, setIosOpen] = useState(false);
  const ios = isIOS();

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;

    // Count visits — only nudge from the 2nd visit onward (returning visitor).
    const visits = (parseInt(localStorage.getItem(VISITS_KEY) || "0", 10) || 0) + 1;
    localStorage.setItem(VISITS_KEY, String(visits));
    const returning = visits >= 2;

    const reveal = () => setShow(true);

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (returning) setTimeout(reveal, 2500);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS never fires beforeinstallprompt — nudge returning Safari users directly.
    let iosTimer;
    if (ios && returning) iosTimer = setTimeout(reveal, 2500);

    const onInstalled = () => { setShow(false); localStorage.setItem(DISMISS_KEY, "1"); };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, [ios]);

  const dismiss = () => { setShow(false); localStorage.setItem(DISMISS_KEY, "1"); };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
      dismiss();
    } else if (ios) {
      setIosOpen(true);
    }
  };

  if (!show) return null;

  return (
    <>
      <div
        data-testid="install-nudge"
        className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:w-[380px] z-[90] rounded-2xl border border-white/12 bg-[#121212]/95 backdrop-blur-xl p-4 shadow-[0_10px_40px_rgba(0,0,0,0.5)] animate-in slide-in-from-bottom-4 fade-in duration-500"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#007AFF]/15 border border-[#007AFF]/30 flex items-center justify-center shrink-0">
            <Download size={18} className="text-[#4da3ff]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-white text-sm font-semibold">Add us to your home screen</p>
            <p className="text-zinc-400 text-xs mt-0.5 leading-relaxed">Install Link Website Pro for one-tap access — it always stays up to date.</p>
            <div className="flex items-center gap-2 mt-3">
              <button data-testid="install-nudge-accept" onClick={install} className="btn-brand text-white rounded-lg px-3.5 py-1.5 text-xs font-medium inline-flex items-center gap-1.5">
                <Download size={13} /> Install
              </button>
              <button data-testid="install-nudge-dismiss" onClick={dismiss} className="text-zinc-500 hover:text-white text-xs transition-colors">Not now</button>
            </div>
          </div>
          <button data-testid="install-nudge-close" onClick={dismiss} className="text-zinc-600 hover:text-white transition-colors shrink-0" aria-label="Dismiss">
            <X size={16} />
          </button>
        </div>
      </div>

      <Dialog open={iosOpen} onOpenChange={(v) => { setIosOpen(v); if (!v) dismiss(); }}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-sm" data-testid="ios-nudge-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Download size={18} /> Install on your iPhone</DialogTitle>
            <DialogDescription className="text-zinc-400">Add Link Website Pro to your Home Screen in two taps.</DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 mt-2 text-sm">
            <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold shrink-0">1</span><span className="flex items-center gap-1.5 text-zinc-200">Tap the <Share size={15} className="text-[#4da3ff]" /> <b>Share</b> button in Safari.</span></li>
            <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold shrink-0">2</span><span className="flex items-center gap-1.5 text-zinc-200">Choose <Plus size={15} className="text-[#4da3ff]" /> <b>Add to Home Screen</b>.</span></li>
          </ol>
        </DialogContent>
      </Dialog>
    </>
  );
}
